// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Button, Center, Divider, Loader, Paper, Stack, Text } from '@mantine/core';
import type { Communication } from '@medplum/fhirtypes';
import { Document } from '@medplum/react';
import { NotificationDropdownItem } from '@polarissleep/messaging';
import { getNotificationHref, NotificationAudience } from '@polarissleep/shared';
import { IconBellOff } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useNavigate } from 'react-router';
import { PageHeader } from '../components/layout/PageHeader';
import { useProviderNotifications } from '../notifications/useProviderNotifications';

export function NotificationsPage(): JSX.Element {
  const { notifications, unreadCount, loading, error, markRead, markAllRead } = useProviderNotifications();
  const navigate = useNavigate();

  const handleMarkAllRead = (): void => {
    markAllRead().catch(() => undefined);
  };

  const handleItemClick = (notification: Communication): void => {
    const href = getNotificationHref(notification, NotificationAudience.PROVIDER);
    markRead(notification).catch(() => undefined);
    if (href) {
      navigate(href);
    }
  };

  const renderBody = (): JSX.Element => {
    if (loading && notifications.length === 0) {
      return (
        <Center p="xl">
          <Loader size="sm" />
        </Center>
      );
    }
    if (notifications.length === 0) {
      return (
        <Stack align="center" gap="xs" p="xl">
          <IconBellOff size={32} stroke={1.5} color="var(--mantine-color-gray-5)" />
          <Text c="dimmed" size="sm">
            You have no notifications.
          </Text>
        </Stack>
      );
    }
    return (
      <>
        {notifications.map((notification, index) => (
          <div key={notification.id}>
            {index > 0 && <Divider />}
            <NotificationDropdownItem notification={notification} onClick={handleItemClick} />
          </div>
        ))}
      </>
    );
  };

  return (
    <Document width={800}>
      <PageHeader
        title="Notifications"
        actions={
          unreadCount > 0 && (
            <Button variant="subtle" size="xs" onClick={handleMarkAllRead}>
              Mark all as read
            </Button>
          )
        }
      />

      {error && (
        <Alert color="red" mb="md">
          Could not load notifications: {error.message}
        </Alert>
      )}

      <Paper withBorder radius="md">
        {renderBody()}
      </Paper>
    </Document>
  );
}
